import { supabaseAdmin } from '../supabase.js';

/**
 * Student Information Service
 * Dedicated service for student information dashboard
 * Handles student browsing, class management, and academic overview
 */
class StudentInformationService {
  constructor(tenantId = 'tn_01') {
    this.tenantId = tenantId;
    this.actualTenantId = this.getActualTenantId();
  }

  getActualTenantId() {
    const tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    };
    return tenantIdMap[this.tenantId];
  }

  /**
   * Get comprehensive student dashboard data
   */
  async getStudentDashboardData() {
    try {
      console.log('Fetching student dashboard data for tenant:', this.tenantId);

      // Get all students
      const { data: students, error: studentsError } = await supabaseAdmin
        .from('saas_students')
        .select('*')
        .eq('tenant_id', this.actualTenantId)
        .order('name');

      if (studentsError) {
        console.error('Error fetching students:', studentsError);
        return { data: null, error: studentsError };
      }

      // Get all classes
      const { data: classes, error: classesError } = await supabaseAdmin
        .from('saas_classes')
        .select('*')
        .eq('tenant_id', this.actualTenantId)
        .order('class_id');

      if (classesError) {
        console.error('Error fetching classes:', classesError);
        return { data: null, error: classesError };
      }

      // Get all teachers with their assignments
      const { data: teachers, error: teachersError } = await supabaseAdmin
        .from('saas_teachers')
        .select(`
          teacher_id,
          name,
          subject_specialty,
          class_master,
          class_vice_master
        `)
        .eq('tenant_id', this.actualTenantId);

      if (teachersError) {
        console.error('Error fetching teachers:', teachersError);
        return { data: null, error: teachersError };
      }

      // Process data
      const processedStudents = students.map(student => {
        const classInfo = classes.find(cls => cls.class_id === student.class_id);
        return {
          ...student,
          classInfo: classInfo || null,
          className: classInfo?.name || student.class_id,
          formLevel: classInfo?.form_level || this.extractFormLevel(student.class_id)
        };
      });

      // Calculate statistics
      const totalStudents = processedStudents.length;
      const averageAttendanceRate = await this.calculateAverageAttendanceRate();

      // Group students by form level
      const studentsByForm = this.groupStudentsByForm(processedStudents);
      
      // Use actual classes from database and add form_level field
      const allClasses = classes.map(cls => ({
        ...cls,
        form_level: this.extractFormLevel(cls.class_id)
      }));
      
      // Get class teacher assignments
      const classTeachers = this.processClassTeachers(teachers, allClasses);
      
      // Define the correct order for form levels
      const formLevelOrder = ['Junior Form 1', 'Junior Form 2', 'Junior Form 3', 'Senior Form 4', 'Senior Form 5', 'Senior Form 6'];
      
      const formLevelStats = formLevelOrder
        .filter(formLevel => studentsByForm[formLevel]) // Only include forms that have students
        .map(formLevel => {
          // Get classes for this form level and sort them by class_id
          const formClasses = allClasses
            .filter(c => c.form_level === formLevel)
            .sort((a, b) => a.class_id.localeCompare(b.class_id)); // Sort classes alphabetically
          
          return {
            formLevel,
            studentCount: studentsByForm[formLevel].length,
            classCount: formClasses.length,
            classes: formClasses.map(c => ({
              classId: c.class_id,
              className: c.name,
              studentCount: studentsByForm[formLevel].filter(s => s.class_id === c.class_id).length,
              teacherInfo: classTeachers[c.class_id] || {
                classMaster: null,
                viceMaster: null,
                subjectTeachers: []
              }
            }))
          };
        });

      return {
        data: {
          totalStudents,
          totalClasses: allClasses.length, // Always 24 classes
          averageAttendanceRate,
          students: processedStudents,
          classes: allClasses,
          studentsByForm,
          formLevelStats,
          classTeachers
        },
        error: null
      };

    } catch (error) {
      console.error('Error in getStudentDashboardData:', error);
      return { data: null, error };
    }
  }

  /**
   * Get students by class
   */
  async getStudentsByClass(classId) {
    try {
      const { data: students, error } = await supabaseAdmin
        .from('saas_students')
        .select('*')
        .eq('class_id', classId)
        .eq('tenant_id', this.actualTenantId)
        .order('name');

      if (error) {
        console.error('Error fetching students by class:', error);
        return { data: null, error };
      }

      // Get class information
      const { data: classInfo, error: classError } = await supabaseAdmin
        .from('saas_classes')
        .select('*')
        .eq('class_id', classId)
        .eq('tenant_id', this.actualTenantId)
        .single();

      if (classError) {
        console.warn('Could not fetch class info:', classError);
      }

      // Get class teachers
      const { data: classTeachers, error: teachersError } = await supabaseAdmin
        .from('saas_teachers')
        .select(`
          teacher_id,
          name,
          subject_specialty,
          class_master,
          class_vice_master
        `)
        .eq('tenant_id', this.actualTenantId);

      if (teachersError) {
        console.warn('Could not fetch class teachers:', teachersError);
      }

      // Filter teachers for this specific class
      const filteredTeachers = (classTeachers || []).filter(teacher => 
        teacher.class_master === classInfo?.id || teacher.class_vice_master === classInfo?.id
      );

      return {
        data: {
          students: students || [],
          classInfo: classInfo || null,
          classTeachers: filteredTeachers,
          studentCount: students?.length || 0
        },
        error: null
      };

    } catch (error) {
      console.error('Error in getStudentsByClass:', error);
      return { data: null, error };
    }
  }

  /**
   * Get detailed student information
   */
  async getStudentDetails(studentId) {
    try {
      const { data: student, error } = await supabaseAdmin
        .from('saas_students')
        .select('*')
        .eq('student_id', studentId)
        .eq('tenant_id', this.actualTenantId)
        .single();

      if (error) {
        console.error('Error fetching student details:', error);
        return { data: null, error };
      }

      if (!student) {
        return { data: null, error: 'Student not found' };
      }

      // Get class information
      const { data: classInfo, error: classError } = await supabaseAdmin
        .from('saas_classes')
        .select('*')
        .eq('class_id', student.class_id)
        .eq('tenant_id', this.actualTenantId)
        .single();

      if (classError) {
        console.warn('Could not fetch class info:', classError);
      }

      // Get academic performance (if available)
      const { data: grades, error: gradesError } = await supabaseAdmin
        .from('student_grades')
        .select('*')
        .eq('student_id', studentId)
        .eq('tenant_id', this.actualTenantId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (gradesError) {
        console.warn('Could not fetch grades:', gradesError);
      }

      // Get attendance records (if available)
      const { data: attendance, error: attendanceError } = await supabaseAdmin
        .from('attendance_records')
        .select('*')
        .eq('student_id', studentId)
        .eq('tenant_id', this.actualTenantId)
        .order('attendance_date', { ascending: false })
        .limit(30);

      if (attendanceError) {
        console.warn('Could not fetch attendance:', attendanceError);
      }

      return {
        data: {
          student: {
            ...student,
            classInfo: classInfo || null,
            className: classInfo?.name || student.class_id,
            formLevel: classInfo?.form_level || this.extractFormLevel(student.class_id)
          },
          academic: {
            grades: grades || [],
            averageGrade: this.calculateAverageGrade(grades || []),
            totalGrades: grades?.length || 0
          },
          attendance: {
            records: attendance || [],
            attendanceRate: this.calculateAttendanceRate(attendance || []),
            recentRecords: attendance?.slice(0, 10) || []
          }
        },
        error: null
      };

    } catch (error) {
      console.error('Error in getStudentDetails:', error);
      return { data: null, error };
    }
  }

  /**
   * Search students by name or student ID
   */
  async searchStudents(query) {
    try {
      if (!query || query.trim().length < 2) {
        return { data: [], error: null };
      }

      const { data: students, error } = await supabaseAdmin
        .from('saas_students')
        .select('*')
        .or(`name.ilike.%${query}%,student_id.ilike.%${query}%`)
        .eq('tenant_id', this.actualTenantId)
        .order('name')
        .limit(20);

      if (error) {
        console.error('Error searching students:', error);
        return { data: null, error };
      }

      return { data: students || [], error: null };

    } catch (error) {
      console.error('Error in searchStudents:', error);
      return { data: null, error };
    }
  }

  /**
   * Group students by form level
   */
  groupStudentsByForm(students) {
    const forms = {};
    students.forEach(student => {
      const formLevel = student.formLevel || this.extractFormLevel(student.class_id);
      if (!forms[formLevel]) {
        forms[formLevel] = [];
      }
      forms[formLevel].push(student);
    });
    return forms;
  }

  /**
   * Process class teachers from teacher data
   */
  processClassTeachers(teachers, classes) {
    const classTeachers = {};
    
    classes.forEach(classData => {
      const classId = classData.id;
      
      // Match by database ID (classId) since that's what the assignment script stores
      const classMaster = teachers.find(t => t.class_master === classId);
      const viceMaster = teachers.find(t => t.class_vice_master === classId);
      
      // For now, we'll show teachers who are not class masters or vice masters as potential subject teachers
      // This is a simplified approach since we don't have detailed subject-class assignments
      const otherTeachers = teachers.filter(teacher => 
        teacher.class_master !== classId &&
        teacher.class_vice_master !== classId &&
        teacher.subject_specialty
      ).slice(0, 3).map(teacher => ({
        name: teacher.name,
        subjects: teacher.subject_specialty || '未指定'
      }));
      
      classTeachers[classData.class_id] = {
        classId: classData.class_id,
        className: classData.name,
        classMaster: classMaster ? {
          name: classMaster.name,
          subject: classMaster.subject_specialty || '未指定'
        } : null,
        viceMaster: viceMaster ? {
          name: viceMaster.name,
          subject: viceMaster.subject_specialty || '未指定'
        } : null,
        subjectTeachers: otherTeachers
      };
    });

    return classTeachers;
  }

  /**
   * Extract form level from class ID
   */
  extractFormLevel(classId) {
    if (!classId) return 'Junior Form 1';
    
    if (classId.includes('J1')) return 'Junior Form 1';
    if (classId.includes('J2')) return 'Junior Form 2';
    if (classId.includes('J3')) return 'Junior Form 3';
    if (classId.includes('S4')) return 'Senior Form 4';
    if (classId.includes('S5')) return 'Senior Form 5';
    if (classId.includes('S6')) return 'Senior Form 6';
    
    return 'Junior Form 1'; // default
  }

  /**
   * Ensure all 24 classes are present (J1A-J3D, S4A-S6D)
   */
  ensureAllClasses(existingClasses) {
    const allClassIds = [
      'J1A', 'J1B', 'J1C', 'J1D',
      'J2A', 'J2B', 'J2C', 'J2D',
      'J3A', 'J3B', 'J3C', 'J3D',
      'S4A', 'S4B', 'S4C', 'S4D',
      'S5A', 'S5B', 'S5C', 'S5D',
      'S6A', 'S6B', 'S6C', 'S6D'
    ];

    const classMap = new Map();
    existingClasses.forEach(cls => {
      classMap.set(cls.class_id, cls);
    });

    // Create missing classes
    const allClasses = allClassIds.map(classId => {
      if (classMap.has(classId)) {
        return classMap.get(classId);
      } else {
        // Create a default class entry for missing classes
        return {
          id: `default-${classId}`,
          class_id: classId,
          name: this.getClassName(classId),
          form_level: this.extractFormLevel(classId),
          tenant_id: this.actualTenantId
        };
      }
    });

    return allClasses;
  }

  /**
   * Get display name for class ID
   */
  getClassName(classId) {
    const formLevel = this.extractFormLevel(classId);
    const classLetter = classId.slice(-1); // Get the last character (A, B, C, D)
    return `${formLevel}${classLetter}`;
  }

  /**
   * Calculate average attendance rate (mock implementation)
   */
  async calculateAverageAttendanceRate() {
    // This would typically query attendance_records table
    // For now, return a mock value
    return 95.2;
  }

  /**
   * Calculate average grade from grades array
   */
  calculateAverageGrade(grades) {
    if (!grades || grades.length === 0) return 0;
    
    const validGrades = grades.filter(g => g.grade && !isNaN(g.grade));
    if (validGrades.length === 0) return 0;
    
    return Math.round(validGrades.reduce((sum, g) => sum + g.grade, 0) / validGrades.length);
  }

  /**
   * Calculate attendance rate from attendance records
   */
  calculateAttendanceRate(attendanceRecords) {
    if (!attendanceRecords || attendanceRecords.length === 0) return 0;
    
    const presentCount = attendanceRecords.filter(a => a.attendance_status === 'present').length;
    return Math.round((presentCount / attendanceRecords.length) * 100);
  }
}

export default StudentInformationService;
